import * as React from 'react'
import {GridToolbarContainer, GridToolbarExport} from '@mui/x-data-grid'
import DataGrid from './ResponsiveDataGrid'
import {Stack, Typography} from '@mui/material'
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined'

// Lists fetched with skipCount pass rowCount={Number.MAX_VALUE}, so the grid can't tell where they
// end and "next" never disables. A short page is the last one: return the exact count there.
export const lastPageRowCount = ({ page, pageSize, rowsOnPage = 0, loading }) =>
  !loading && rowsOnPage < pageSize ? page * pageSize + rowsOnPage : Number.MAX_VALUE

// Back to the first page when `trigger` (e.g. the applied filters) changes. Resets during render
// (React's "adjust state on prop change" pattern), so the list fetches once, already on page 0.
export function useFirstPageOnChange(trigger, setPage) {
  const [prev, setPrev] = React.useState(trigger)
  if (prev !== trigger) {
    setPrev(trigger)
    setPage(0)
  }
}

export default function ProzoDataGrid(props) {
  let {
    rows,
    columns,
    columnVisibilityModel,
    disableColumnMenu,
    pagination,
    paginationMode,
    checkboxSelection = false,
    rowSelectionModel,
    setRowSelectionModel,
    keepNonExistentRowsSelected,
    disableColumnSelector,
    rowSpacing = true,
    hideFooterPagination,
    disableColumnFilter,
    rowCount,
    page,
    setPage,
    sizePerPage,
    setSizePerPage,
    hideCountNumber,
    boxShadow = 1,
    exportFileName = 'proship',
    // Opt-in fixed-height mode: when a caller passes containerHeight, the grid stops
    // auto-sizing to its rows and scrolls internally instead. Prevents the container
    // (e.g. a modal) from resizing while auto row-heights are measured on page change.
    autoHeight = true,
    containerHeight,
    // Opt-in fixed row height. Skips MUI's 'auto' row-height measurement pass, so the
    // grid sizes deterministically (no flicker) while still fitting its content.
    rowHeight,
    // The page size must be one of these, or MUI hides the rows-per-page selector.
    rowsPerPageOptions = [10, 15, 20, 50, 100],
    ...restProps
  } = props

  const isEmpty = !rows || rows.length === 0
  const getRowSpacing = React.useCallback((params) => {
    return {
      top: params.isFirstVisible ? 0 : 5,
      bottom: params.isLastVisible ? 0 : 5,
    }
  }, [])

  return (
    <DataGrid
      columns={columns}
      style={{background: 'white', width: '100%'}}
      getRowHeight={rowHeight ? undefined : () => 'auto'}
      rowHeight={rowHeight}
      rows={rows}
      disableSelectionOnClick={true}
      rowCount={rowCount}
      checkboxSelection={checkboxSelection}
      selectionModel={rowSelectionModel}
      onSelectionModelChange={(newRowSelectionModel) => {
        setRowSelectionModel(newRowSelectionModel)
      }}
      keepNonExistentRowsSelected={keepNonExistentRowsSelected}
      paginationMode={paginationMode ? paginationMode : 'server'}
      rowsPerPageOptions={rowsPerPageOptions}
      pagination={pagination ? pagination : true}
      columnVisibilityModel={columnVisibilityModel ? columnVisibilityModel : {}}
      hideFooterPagination={hideFooterPagination ? hideFooterPagination : false}
      disableColumnFilter={disableColumnFilter ? disableColumnFilter : true}
      disableColumnMenu={disableColumnMenu ? disableColumnMenu : true}
      disableColumnSelector={disableColumnSelector ? disableColumnSelector : true}
      onPageSizeChange={setSizePerPage}
      onPageChange={setPage}
      autoHeight={containerHeight ? false : autoHeight}
      getRowSpacing={rowSpacing ? getRowSpacing : ''}
      page={page}
      pageSize={sizePerPage}
      getRowId={props.getRowId}
      sx={{
        borderRadius: 0,
        boxShadow,
        fontSize: 12,
        ...(containerHeight ? { height: containerHeight } : {}),
        '& .MuiTablePagination-root': { fontSize: 12 },
        '& .MuiTablePagination-selectLabel': { fontSize: 12 },
        '& .MuiTablePagination-displayedRows': { fontSize: 12 },
        '& .MuiTablePagination-select': { fontSize: 12 },
        '& .MuiCheckbox-root': { p: 0.5 },
        '& .MuiCheckbox-root .MuiSvgIcon-root': { fontSize: 18 },
        '& .MuiDataGrid-cell': { whiteSpace: 'normal', wordBreak: 'break-word' },
        // When empty, force the scroller (which the overlay overlays) to have height so the
        // "No results found" overlay is visible. Under autoHeight v5 collapses it to 0 otherwise.
        '& .MuiDataGrid-virtualScroller': { overflowX: 'auto', ...(isEmpty ? { minHeight: 200 } : {}) },
      }}
      components={getComponents(props.exportToExcel || false, exportFileName)}
      localeText={{
        MuiTablePagination: {
          labelDisplayedRows: ({from, to, count}) => {
            if (hideCountNumber) {
              return from + '-' + to + ' of many'
            }
            return `${from} - ${to} of ${count === Number.MAX_VALUE ? 'many' : count}`
          },
        },
      }}
      componentsProps={{
        toolbar: {
          printOptions: {disableToolbarButton: true},
        },
      }}
      {...restProps}
    />
  )
}

function EmptyOverlay() {
  return (
    <Stack
      spacing={1}
      alignItems='center'
      justifyContent='center'
      sx={{ minHeight: 200, height: '100%', width: '100%', color: 'text.secondary', py: 4 }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, color: 'grey.400' }} />
      <Typography sx={{ fontSize: 15, fontWeight: 600, color: 'text.primary' }}>
        No results found!
      </Typography>
      <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
        There are no records to display here.
      </Typography>
    </Stack>
  )
}

function getComponents(exportToExcel, exportFileName) {
  let options = {
    NoRowsOverlay: EmptyOverlay,
    NoResultsOverlay: EmptyOverlay,
  }

  if (exportToExcel) {
    options.Toolbar = () => <CustomToolbar exportFileName={exportFileName} />
  }

  return options
}

function CustomToolbar({exportFileName}) {
  return (
    <GridToolbarContainer>
      <GridToolbarExport csvOptions={{fileName: exportFileName}} />
    </GridToolbarContainer>
  )
}
